import { FC } from 'react';
import { IonButton, IonContent, IonModal } from '@ionic/react';
import { createUseStyles } from 'react-jss';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { MixPanelEventEmitter } from '../../metrics/MixPanelEventEmitter';
import { DuoCloudArrowUpIcon } from '../../theme/Icons';

const useStyles = createUseStyles({
    ionContent: {
        '--padding-top': '0',
        '--padding-bottom': '0',
        height: '100%',
        display: 'flex'
    },

    content: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '48px 24px 32px 24px',
        textAlign: 'center'
    },

    uploadIcon: {
        marginBottom: 24,
    },

    warningIcon: {
        width: 64,
        height: 64,
        backgroundColor: '#F59E0B',
        borderRadius: 12,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 24,
        fontSize: 32,
        color: 'white'
    },

    title: {
        fontSize: 18,
        fontWeight: 600,
        color: 'var(--ion-color-dark)',
        marginBottom: 16
    },

    message: {
        fontSize: 14,
        marginTop: 12,
        marginBottom: 40,
        lineHeight: 1.4,
        maxWidth: 280,
        color: 'var(--ion-color-medium)'
    },

    ellipsisContainer: {
        marginBottom: 48,
        marginTop: 16,
        display: 'flex',
        gap: 4,
        justifyContent: 'center'
    },

    dot: {
        width: 10,
        height: 10,
        borderRadius: '50%',
        backgroundColor: 'var(--ion-color-primary)',
        animation: '$blink 1.4s infinite both',

        '&:nth-child(1)': {
            animationDelay: '0s'
        },
        '&:nth-child(2)': {
            animationDelay: '0.2s'
        },
        '&:nth-child(3)': {
            animationDelay: '0.4s'
        }
    },

    '@keyframes blink': {
        '0%': {
            opacity: 0.2,
            transform: 'scale(0.8)'
        },
        '20%': {
            opacity: 1,
            transform: 'scale(1)'
        },
        '100%': {
            opacity: 0.2,
            transform: 'scale(0.8)'
        }
    },

    button: {
        '--padding-start': '32px',
        '--padding-end': '32px',
        '--padding-top': '12px',
        '--padding-bottom': '12px',
        fontSize: 16,
        '--border-radius': '8px',
        width: '100%',
        maxWidth: 280
    }
});

interface IRecordingDirectUploadModalProps {
    isOpen: boolean;
    status: 'uploading' | 'error';
    recordingId: string | null;
    onRetry: () => void;
}

export const RecordingDirectUploadModal: FC<IRecordingDirectUploadModalProps> = ({
    isOpen,
    status,
    recordingId,
    onRetry
}) => {
    const classes = useStyles();
    const mixpanel = useInstance(MixPanelEventEmitter);

    const handleRetry = () => {
        mixpanel.track('Direct Upload Manual Retry', () => ({
            'Recording Id': recordingId
        }));
        onRetry();
    };

    const isUploading = status === 'uploading';

    return (
        <IonModal
            isOpen={isOpen}
            backdropDismiss={false}
            data-id="recording-direct-upload-modal"
            data-status={status}
        >
            <IonContent className={classes.ionContent}>
                <div className={classes.content}>
                    {isUploading ? (
                        <>
                            <div className={classes.uploadIcon} data-id="upload-icon">
                                <DuoCloudArrowUpIcon color="success" customSize={48} />
                            </div>

                            <div className={classes.title} data-id="direct-upload-title">
                                Uploading Recording
                            </div>

                            <div className={classes.message}>
                                Please don't close the app until upload completes.
                                Your recording is being saved directly to the cloud.
                            </div>

                            <div className={classes.ellipsisContainer}>
                                <div className={classes.dot}></div>
                                <div className={classes.dot}></div>
                                <div className={classes.dot}></div>
                            </div>
                        </>
                    ) : (
                        <>
                            <div className={classes.warningIcon} data-id="warning-icon">
                                !
                            </div>

                            <div className={classes.title} data-id="direct-upload-title">
                                Upload Waiting
                            </div>

                            <div className={classes.message}>
                                You seem to have lost your network connection, the upload will automatically resume when your connection is restored.
                                If it's already restored, tap below to try now.
                            </div>

                            <IonButton
                                expand="block"
                                className={classes.button}
                                onClick={handleRetry}
                                data-id="retry-button"
                            >
                                Retry Now
                            </IonButton>
                        </>
                    )}
                </div>
            </IonContent>
        </IonModal>
    );
};
